// RGB转十六进制
export function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(x => {
    const hex = x.toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }).join('')
}

// 十六进制转RGB
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null
}

// 从Canvas获取像素颜色
export function getPixelColor(
  canvas: HTMLCanvasElement,
  x: number,
  y: number
): { r: number; g: number; b: number; hex: string } | null {
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  const imageData = ctx.getImageData(x, y, 1, 1)
  const data = imageData.data

  return {
    r: data[0],
    g: data[1],
    b: data[2],
    hex: rgbToHex(data[0], data[1], data[2])
  }
}

// 从图片提取颜色网格
export function extractColorGrid(
  image: HTMLImageElement,
  gridWidth: number,
  gridHeight: number
): Array<Array<{ r: number; g: number; b: number; hex: string }>> {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return []

  canvas.width = gridWidth
  canvas.height = gridHeight

  // 绘制图片到canvas
  ctx.drawImage(image, 0, 0, gridWidth, gridHeight)

  const colors: Array<Array<{ r: number; g: number; b: number; hex: string }>> = []

  for (let y = 0; y < gridHeight; y++) {
    const row: Array<{ r: number; g: number; b: number; hex: string }> = []
    for (let x = 0; x < gridWidth; x++) {
      const color = getPixelColor(canvas, x, y)
      if (color) {
        row.push(color)
      }
    }
    colors.push(row)
  }

  return colors
}

// 异步提取颜色网格（带进度回调，避免卡顿）
export async function extractColorGridAsync(
  image: HTMLImageElement,
  gridWidth: number,
  gridHeight: number,
  onProgress?: (progress: number) => void
): Promise<Array<Array<{ r: number; g: number; b: number; hex: string }>>> {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) { resolve([]); return }

    canvas.width = gridWidth
    canvas.height = gridHeight
    ctx.drawImage(image, 0, 0, gridWidth, gridHeight)

    const colors: Array<Array<{ r: number; g: number; b: number; hex: string }>> = new Array(gridHeight)
    const total = gridHeight
    let y = 0

    function step() {
      const batch = 4 // 每帧处理的行数
      const endY = Math.min(gridHeight, y + batch)
      for (; y < endY; y++) {
        const row: Array<{ r: number; g: number; b: number; hex: string }> = new Array(gridWidth)
        for (let x = 0; x < gridWidth; x++) {
          const c = getPixelColor(canvas, x, y)!
          row[x] = c
        }
        colors[y] = row
      }
      if (onProgress) onProgress(Math.min(1, endY / total))
      if (y < gridHeight) requestAnimationFrame(step)
      else resolve(colors)
    }
    step()
  })
}

// 生成预览图片（异步版本）
export async function generatePreviewImage(
  colors: Array<Array<{ r: number; g: number; b: number; hex: string }>>,
  pixelSize: number = 4,
  customColors?: Array<{ r: number; g: number; b: number; hex: string; name: string; id: number }>,
  algorithm: 'rgb' | 'lab' | 'weighted-rgb' | 'hsv' | 'ciede2000' = 'rgb',
  options?: { 
    mergeColor?:{
      mergeMode:'rgb' | 'cielab',
      mergeThreshold: number
    }, 
    baseFabricColor?: { r: number; g: number; b: number; hex: string },
    dithering?: 'none' | 'floyd-steinberg'; 
    strength?: number; 
    gamma?: number; // 典型 1.0（无校正），>1.0 提升暗部，<1.0 压制暗部
    brightness?: number; // -1..1，整体亮度偏移
    constraints?: { 
      clampHighlights?: number; // 0..1，高光压制强度
      liftShadows?: number;     // 0..1，阴影提升强度
      saturationBoost?: number; // -1..1，饱和度微调
    }
    posterizeLevels?: number;   // 每通道量化等级（2-32）；未设置或<=1表示关闭
    // 新增：前处理
    downscaleRatio?: number; // 0.1..1，先缩小后像素化（通过网格级重采样模拟）
    denoiseStrength?: number; // 0..1，中值滤波次数：round(denoiseStrength*2)
    edgePreserveStrength?: number; // 0..1，双边滤波次数：round(edgePreserveStrength*2)
    preQuantizeLevels?: number; // 0..32，前置均匀量化（颜色简化）
    // 新增：像素回调
    onOriginal?: (x: number, y: number, hex: string, rgb: { r: number; g: number; b: number }) => void;
    onMatch?: (x: number, y: number, match: { id: number; name: string; hex: string; r: number; g: number; b: number }) => void;
    // 新增：进度回调
    onProgress?: (progress: number) => void;
    // 新增：归并结果回调
    onMergeResult?: (result: { originalCount: number; mergedCount: number; reduction: number }) => void;
    // 新增：归并后颜色数据回调
    onMergedColors?: (mergedColors: Array<Array<{ r: number; g: number; b: number; hex: string }>>) => void;
    // 新增：整体矩阵回调（完成后一次性返回最终颜色矩阵）
    onRenderedMatrix?: (matrix: Array<Array<{ r: number; g: number; b: number; hex: string; id?: number; name?: string }>>) => void;
  }
): Promise<string> {
  return new Promise((resolve) => {
    // 使用requestAnimationFrame来避免阻塞UI
    requestAnimationFrame(() => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        resolve('')
        return
      }

      // 工作副本，使用浅拷贝避免修改原数组
      const srcColors = colors
      let workColors: Array<Array<{ r: number; g: number; b: number; hex: string }>> = srcColors.map(row => 
        row.map(pixel => ({ ...pixel }))
      )

      const origWidth = workColors[0]?.length || 0
      const origHeight = workColors.length

      // 工具函数
      const toHex = (r: number, g: number, b: number) => rgbToHex(r, g, b)
      const clamp8 = (v: number) => Math.max(0, Math.min(255, Math.round(v)))

      // 前处理：先压缩再像素化（网格级近似）
      const downscaleRatio = Math.max(0.1, Math.min(1, options?.downscaleRatio ?? 1))
      if (downscaleRatio < 1 && origWidth > 0 && origHeight > 0) {
        const smallW = Math.max(1, Math.round(origWidth * downscaleRatio))
        const smallH = Math.max(1, Math.round(origHeight * downscaleRatio))
        // 盒式平均降采样
        const small: Array<Array<{ r: number; g: number; b: number; hex: string }>> = Array.from({ length: smallH }, () => Array.from({ length: smallW }, () => ({ r: 0, g: 0, b: 0, hex: '#000000' })))
        const boxW = origWidth / smallW
        const boxH = origHeight / smallH
        for (let y = 0; y < smallH; y++) {
          for (let x = 0; x < smallW; x++) {
            const x0 = Math.floor(x * boxW)
            const x1 = Math.min(origWidth, Math.floor((x + 1) * boxW))
            const y0 = Math.floor(y * boxH)
            const y1 = Math.min(origHeight, Math.floor((y + 1) * boxH))
            let ar = 0, ag = 0, ab = 0, n = 0
            for (let yy = y0; yy < y1; yy++) {
              for (let xx = x0; xx < x1; xx++) {
                const c = workColors[yy][xx]
                ar += c.r; ag += c.g; ab += c.b; n++
              }
            }
            if (n > 0) {
              const r = clamp8(ar / n), g = clamp8(ag / n), b = clamp8(ab / n)
              small[y][x] = { r, g, b, hex: toHex(r, g, b) }
            }
          }
        }
        // 最近邻上采样回原网格
        const up: typeof workColors = Array.from({ length: origHeight }, () => Array.from({ length: origWidth }, () => ({ r: 0, g: 0, b: 0, hex: '#000000' })))
        for (let y = 0; y < origHeight; y++) {
          for (let x = 0; x < origWidth; x++) {
            const sx = Math.min(smallW - 1, Math.floor(x * smallW / origWidth))
            const sy = Math.min(smallH - 1, Math.floor(y * smallH / origHeight))
            const c = small[sy][sx]
            up[y][x] = c
          }
        }
        workColors = up
      }

      // 前处理：中值降噪（3x3）
      const denoiseIters = Math.max(0, Math.round((options?.denoiseStrength ?? 0) * 2))
      if (denoiseIters > 0) {
        const median = (arr: number[]) => {
          const a = arr.slice().sort((a, b) => a - b)
          return a[(a.length / 2) | 0]
        }
        for (let it = 0; it < denoiseIters; it++) {
          const out: typeof workColors = workColors.map(row => row.slice())
          for (let y = 0; y < origHeight; y++) {
            for (let x = 0; x < origWidth; x++) {
              const rs: number[] = [], gs: number[] = [], bs: number[] = []
              for (let dy = -1; dy <= 1; dy++) {
                for (let dx = -1; dx <= 1; dx++) {
                  const yy = Math.max(0, Math.min(origHeight - 1, y + dy))
                  const xx = Math.max(0, Math.min(origWidth - 1, x + dx))
                  const c = workColors[yy][xx]
                  rs.push(c.r); gs.push(c.g); bs.push(c.b)
                }
              }
              const r = clamp8(median(rs)), g = clamp8(median(gs)), b = clamp8(median(bs))
              out[y][x] = { r, g, b, hex: toHex(r, g, b) }
            }
          }
          workColors = out
        }
      }

      // 前处理：简单双边滤波（边缘保留）
      const edgeIters = Math.max(0, Math.round((options?.edgePreserveStrength ?? 0) * 2))
      if (edgeIters > 0) {
        const spatialSigma = 1.0
        const colorSigma = 32.0
        const wSpatial = (dx: number, dy: number) => Math.exp(-(dx*dx + dy*dy) / (2 * spatialSigma * spatialSigma))
        const wColor = (dr: number, dg: number, db: number) => {
          const d2 = dr*dr + dg*dg + db*db
          return Math.exp(-d2 / (2 * colorSigma * colorSigma))
        }
        for (let it = 0; it < edgeIters; it++) {
          const out: typeof workColors = workColors.map(row => row.slice())
          for (let y = 0; y < origHeight; y++) {
            for (let x = 0; x < origWidth; x++) {
              const center = workColors[y][x]
              let wr = 0, wg = 0, wb = 0, wsum = 0
              for (let dy = -1; dy <= 1; dy++) {
                for (let dx = -1; dx <= 1; dx++) {
                  const yy = Math.max(0, Math.min(origHeight - 1, y + dy))
                  const xx = Math.max(0, Math.min(origWidth - 1, x + dx))
                  const c = workColors[yy][xx]
                  const ws = wSpatial(dx, dy)
                  const wc = wColor(c.r - center.r, c.g - center.g, c.b - center.b)
                  const w = ws * wc
                  wr += c.r * w; wg += c.g * w; wb += c.b * w; wsum += w
                }
              }
              const r = clamp8(wr / (wsum || 1)), g = clamp8(wg / (wsum || 1)), b = clamp8(wb / (wsum || 1))
              out[y][x] = { r, g, b, hex: toHex(r, g, b) }
            }
          }
          workColors = out
        }
      }

      // 前处理：均匀量化（颜色简化，前置）
      const preLevels = Math.max(0, Math.floor(options?.preQuantizeLevels ?? 0))
      if (preLevels > 1) {
        const step = 255 / (preLevels - 1)
        const quant = (v: number) => clamp8(Math.round(v / step) * step)
        for (let y = 0; y < origHeight; y++) {
          for (let x = 0; x < origWidth; x++) {
            const c = workColors[y][x]
            const r = quant(c.r), g = quant(c.g), b = quant(c.b)
            workColors[y][x] = { r, g, b, hex: toHex(r, g, b) }
          }
        }
      }

      const width = origWidth
      const height = origHeight

      // 在渲染前执行颜色归并（如果提供了归并参数）
      if (options?.mergeColor) {
        const { mergeMode, mergeThreshold } = options.mergeColor
        console.log('开始执行颜色归并...', { mergeMode, mergeThreshold })
        
        // 收集所有唯一颜色
        const colorMap = new Map<string, { r: number; g: number; b: number; hex: string }>()
        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            const pixel = workColors[y][x]
            if (!colorMap.has(pixel.hex)) {
              colorMap.set(pixel.hex, { r: pixel.r, g: pixel.g, b: pixel.b, hex: pixel.hex })
            }
          }
        }
        
        const uniqueColors = Array.from(colorMap.values())
        console.log(`收集到 ${uniqueColors.length} 种唯一颜色`)
        
        // 对颜色进行分组归并
        const colorGroups: Array<{
          representative: { r: number; g: number; b: number; hex: string }
          similar: { r: number; g: number; b: number; hex: string }[]
        }> = []
        
        const processed = new Set<string>()
        
        for (const color of uniqueColors) {
          if (processed.has(color.hex)) continue
          
          // 找到所有相似的颜色
          const similar = [color]
          processed.add(color.hex)
          
          for (const otherColor of uniqueColors) {
            if (processed.has(otherColor.hex)) continue
            
            let distance: number
            if (mergeMode === 'rgb') {
              // RGB 色差阈值：计算RGB差距百分比
              const rDiff = Math.abs(color.r - otherColor.r)
              const gDiff = Math.abs(color.g - otherColor.g)
              const bDiff = Math.abs(color.b - otherColor.b)
              const maxDiff = Math.max(rDiff, gDiff, bDiff)
              distance = (maxDiff / 255) * 100 // 转换为百分比
            } else {
              // CIELAB 色差阈值：使用 ΔE
              distance = colorDistanceLab(color, otherColor)
            }
            
            if (distance <= mergeThreshold) {
              similar.push(otherColor)
              processed.add(otherColor.hex)
            }
          }
          
          // 选择代表色（这里简单选择第一个）
          const representative = similar[0]
          
          colorGroups.push({
            representative,
            similar
          })
        }
        
        // 创建颜色映射表
        const mergeMap = new Map<string, { r: number; g: number; b: number; hex: string }>()
        for (const group of colorGroups) {
          for (const similarColor of group.similar) {
            mergeMap.set(similarColor.hex, group.representative)
          }
        }
        
        // 应用归并结果到工作颜色数组，并优先与底色比较
        const base = options?.baseFabricColor || null
        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            const pixel = workColors[y][x]
            let mapped = mergeMap.get(pixel.hex)
            if (base) {
              // 计算与底色的距离
              const dist = (mergeMode === 'rgb')
                ? (Math.max(
                    Math.abs(pixel.r - base.r),
                    Math.abs(pixel.g - base.g),
                    Math.abs(pixel.b - base.b)
                  ) / 255) * 100
                : colorDistanceLab(pixel, base)
              if (dist <= mergeThreshold) {
                mapped = base
              }
            }
            if (mapped) {
              pixel.r = mapped.r
              pixel.g = mapped.g
              pixel.b = mapped.b
              pixel.hex = mapped.hex
            }
          }
        }
        
        console.log(`颜色归并完成：${uniqueColors.length} -> ${colorGroups.length} 种颜色`)
        
        // 调用归并结果回调
        if (options?.onMergeResult) {
          const reduction = Math.round((1 - colorGroups.length / uniqueColors.length) * 100)
          options.onMergeResult({
            originalCount: uniqueColors.length,
            mergedCount: colorGroups.length,
            reduction
          })
        }
      }

      canvas.width = width * pixelSize
      canvas.height = height * pixelSize

      const useDither = (options?.dithering ?? 'none') !== 'none' && !!customColors && customColors.length > 0
      const ditherStrength = Math.max(0, Math.min(1, options?.strength ?? 1))
      // 最终矩阵（若需要则构建）
      const finalMatrix: Array<Array<{ r: number; g: number; b: number; hex: string; id?: number; name?: string }>> | null = options?.onRenderedMatrix
        ? Array.from({ length: height }, () => Array.from({ length: width }, () => ({ r: 0, g: 0, b: 0, hex: '#000000' })))
        : null

      // 预处理：Gamma/亮度 + 约束（作用于渲染阶段）
      const gamma = Math.max(0.1, Math.min(5, options?.gamma ?? 1))
      const brightness = Math.max(-1, Math.min(1, options?.brightness ?? 0))
      const clampHighlights = Math.max(0, Math.min(1, options?.constraints?.clampHighlights ?? 0))
      const liftShadows = Math.max(0, Math.min(1, options?.constraints?.liftShadows ?? 0))
      const saturationBoost = Math.max(-1, Math.min(1, options?.constraints?.saturationBoost ?? 0))

      function applyPreprocess(rgb: { r: number; g: number; b: number }): { r: number; g: number; b: number } {
        // 归一化
        let r = rgb.r / 255, g = rgb.g / 255, b = rgb.b / 255
        // gamma
        r = Math.pow(r, 1 / gamma)
        g = Math.pow(g, 1 / gamma)
        b = Math.pow(b, 1 / gamma)
        // 亮度偏移
        r = Math.min(1, Math.max(0, r + brightness))
        g = Math.min(1, Math.max(0, g + brightness))
        b = Math.min(1, Math.max(0, b + brightness))
        // 高光/阴影
        const compressHighlight = (v: number) => v - clampHighlights * Math.max(0, v - 0.8) / 0.2 * (v - 0.8)
        const liftShadow = (v: number) => v + liftShadows * Math.max(0, 0.2 - v) / 0.2 * (0.2 - v)
        r = liftShadow(compressHighlight(r))
        g = liftShadow(compressHighlight(g))
        b = liftShadow(compressHighlight(b))
        // 饱和度
        const max = Math.max(r, g, b), min = Math.min(r, g, b)
        const l = (max + min) / 2
        // 计算饱和度（此处仅用于中间变量描述）
        // const sat = max === min ? 0 : (l > 0.5 ? (max - min) / (2 - max - min) : (max - min) / (max + min))
        const boost = 1 + saturationBoost
        const gray = l
        r = gray + (r - gray) * boost
        g = gray + (g - gray) * boost
        b = gray + (b - gray) * boost
        // 海报化量化（每通道）
        const levels = Math.max(0, Math.floor(options?.posterizeLevels ?? 0))
        if (levels > 1) {
          const step = 1 / (levels - 1)
          const quant = (v: number) => Math.round(v / step) * step
          r = Math.min(1, Math.max(0, quant(r)))
          g = Math.min(1, Math.max(0, quant(g)))
          b = Math.min(1, Math.max(0, quant(b)))
        }
        return { r: Math.round(Math.min(255, Math.max(0, r * 255))), g: Math.round(Math.min(255, Math.max(0, g * 255))), b: Math.round(Math.min(255, Math.max(0, b * 255))) }
      }

      if (useDither) {
        // Floyd–Steinberg 抖动（基于当前网格像素）
        const errR = new Float32Array(width * height)
        const errG = new Float32Array(width * height)
        const errB = new Float32Array(width * height)
        const idx = (x: number, y: number) => y * width + x
        const clamp = (v: number) => Math.max(0, Math.min(255, v))

        const processRow = (y: number) => {
          for (let x = 0; x < width; x++) {
            const src = applyPreprocess(workColors[y][x])
            const i = idx(x, y)
            let r = clamp(src.r + errR[i])
            let g = clamp(src.g + errG[i])
            let b = clamp(src.b + errB[i])

            const target = { r: Math.round(r), g: Math.round(g), b: Math.round(b) }
            const chosen = findClosestColor(target, customColors!, algorithm)

            // 收集最终矩阵
            if (finalMatrix) {
              finalMatrix[y][x] = { r: chosen.r, g: chosen.g, b: chosen.b, hex: chosen.hex, id: chosen.id, name: chosen.name }
            }

            ctx!.fillStyle = chosen.hex
            ctx!.fillRect(x * pixelSize, y * pixelSize, pixelSize, pixelSize)

            const er = (r - chosen.r) * ditherStrength
            const eg = (g - chosen.g) * ditherStrength
            const eb = (b - chosen.b) * ditherStrength

            if (x + 1 < width) { const j = idx(x + 1, y); errR[j] += er * 7 / 16; errG[j] += eg * 7 / 16; errB[j] += eb * 7 / 16 }
            if (x - 1 >= 0 && y + 1 < height) { const j = idx(x - 1, y + 1); errR[j] += er * 3 / 16; errG[j] += eg * 3 / 16; errB[j] += eb * 3 / 16 }
            if (y + 1 < height) { const j = idx(x, y + 1); errR[j] += er * 5 / 16; errG[j] += eg * 5 / 16; errB[j] += eb * 5 / 16 }
            if (x + 1 < width && y + 1 < height) { const j = idx(x + 1, y + 1); errR[j] += er * 1 / 16; errG[j] += eg * 1 / 16; errB[j] += eb * 1 / 16 }
          }
        }

        let currentY = 0
        const stepFrame = () => {
          const endY = Math.min(currentY + 4, height)
          for (let y = currentY; y < endY; y++) processRow(y)
          currentY = endY
          // 更新进度
          const progress = currentY / height
          options?.onProgress?.(progress)
          if (currentY < height) requestAnimationFrame(stepFrame)
          else {
            if (finalMatrix && options?.onRenderedMatrix) options.onRenderedMatrix(finalMatrix)
            resolve(canvas.toDataURL('image/png'))
          }
        }
        stepFrame()
      } else {
        const batchSize = 1000
        let currentIndex = 0
        const totalPixels = width * height

        function processBatch() {
          const endIndex = Math.min(currentIndex + batchSize, totalPixels)
          for (let i = currentIndex; i < endIndex; i++) {
            const y = Math.floor(i / width)
            const x = i % width
            const color = applyPreprocess(workColors[y][x])
            if (color) {
              let fillColor = rgbToHex(color.r, color.g, color.b)
              if (customColors && customColors.length > 0) {
                const closestColor = findClosestColor(color, customColors, algorithm)
                fillColor = closestColor.hex
                if (finalMatrix) {
                  finalMatrix[y][x] = { r: closestColor.r, g: closestColor.g, b: closestColor.b, hex: closestColor.hex, id: closestColor.id, name: closestColor.name }
                }
              } else if (finalMatrix) {
                finalMatrix[y][x] = { r: color.r, g: color.g, b: color.b, hex: fillColor }
              }
              ctx!.fillStyle = fillColor
              ctx!.fillRect(x * pixelSize, y * pixelSize, pixelSize, pixelSize)
            }
          }
          currentIndex = endIndex
          // 更新进度
          const progress = currentIndex / totalPixels
          options?.onProgress?.(progress)
          if (currentIndex < totalPixels) requestAnimationFrame(processBatch)
          else {
            if (finalMatrix && options?.onRenderedMatrix) options.onRenderedMatrix(finalMatrix)
            resolve(canvas.toDataURL('image/png'))
          }
        }
        processBatch()
      }
    })
  })
}

// 在自定义颜色中找到最相近的颜色
export function findClosestColor(
  targetColor: { r: number; g: number; b: number },
  customColors: Array<{ r: number; g: number; b: number; hex: string; name: string; id: number }>,
  algorithm: 'rgb' | 'lab' | 'weighted-rgb' | 'hsv' | 'ciede2000' = 'rgb'
): { r: number; g: number; b: number; hex: string; name: string; id: number } {
  if (customColors.length === 0) {
    throw new Error('没有自定义颜色可供匹配')
  }

  let closestColor = customColors[0]
  let minDistance = getColorDistance(targetColor, closestColor, algorithm)

  for (let i = 1; i < customColors.length; i++) {
    const distance = getColorDistance(targetColor, customColors[i], algorithm)
    
    if (distance < minDistance) {
      minDistance = distance
      closestColor = customColors[i]
    }
  }

  return closestColor
}

// 根据算法计算颜色距离
export function getColorDistance(
  color1: { r: number; g: number; b: number },
  color2: { r: number; g: number; b: number },
  algorithm: 'rgb' | 'lab' | 'weighted-rgb' | 'hsv' | 'ciede2000'
): number {
  switch (algorithm) {
    case 'rgb':
      return colorDistance(color1, color2)
    case 'lab':
      return colorDistanceLab(color1, color2)
    case 'weighted-rgb':
      return colorDistanceWeightedRGB(color1, color2)
    case 'hsv':
      return colorDistanceHSV(color1, color2)
    case 'ciede2000':
      return colorDistanceCIEDE2000(color1, color2)
    default:
      return colorDistance(color1, color2)
  }
}

// 计算两个颜色之间的欧几里得距离（RGB空间）
export function colorDistance(
  color1: { r: number; g: number; b: number },
  color2: { r: number; g: number; b: number }
): number {
  return Math.sqrt(
    Math.pow(color1.r - color2.r, 2) +
    Math.pow(color1.g - color2.g, 2) +
    Math.pow(color1.b - color2.b, 2)
  )
}

// RGB转XYZ色彩空间
function rgbToXyz(r: number, g: number, b: number): { x: number; y: number; z: number } {
  // 归一化RGB值到[0,1]
  const rNorm = r / 255
  const gNorm = g / 255
  const bNorm = b / 255

  // 应用gamma校正
  const rGamma = rNorm > 0.04045 ? Math.pow((rNorm + 0.055) / 1.055, 2.4) : rNorm / 12.92
  const gGamma = gNorm > 0.04045 ? Math.pow((gNorm + 0.055) / 1.055, 2.4) : gNorm / 12.92
  const bGamma = bNorm > 0.04045 ? Math.pow((bNorm + 0.055) / 1.055, 2.4) : bNorm / 12.92

  // 转换为XYZ
  const x = rGamma * 0.4124 + gGamma * 0.3576 + bGamma * 0.1805
  const y = rGamma * 0.2126 + gGamma * 0.7152 + bGamma * 0.0722
  const z = rGamma * 0.0193 + gGamma * 0.1192 + bGamma * 0.9505

  return { x, y, z }
}

// XYZ转CIELAB色彩空间
function xyzToLab(x: number, y: number, z: number): { l: number; a: number; b: number } {
  // D65标准光源
  const xn = 0.95047
  const yn = 1.0
  const zn = 1.08883

  const xNorm = x / xn
  const yNorm = y / yn
  const zNorm = z / zn

  const fx = xNorm > 0.008856 ? Math.pow(xNorm, 1/3) : (7.787 * xNorm) + (16 / 116)
  const fy = yNorm > 0.008856 ? Math.pow(yNorm, 1/3) : (7.787 * yNorm) + (16 / 116)
  const fz = zNorm > 0.008856 ? Math.pow(zNorm, 1/3) : (7.787 * zNorm) + (16 / 116)

  const l = (116 * fy) - 16
  const a = 500 * (fx - fy)
  const b = 200 * (fy - fz)

  return { l, a, b }
}

// RGB转CIELAB
export function rgbToLab(r: number, g: number, b: number): { l: number; a: number; b: number } {
  const xyz = rgbToXyz(r, g, b)
  return xyzToLab(xyz.x, xyz.y, xyz.z)
}

// =============== LAB ↔ XYZ ↔ RGB 反向转换 ===============
function labToXyz(L: number, a: number, b: number): { x: number; y: number; z: number } {
  const y = (L + 16) / 116
  const x = a / 500 + y
  const z = y - b / 200
  const xn = 0.95047
  const yn = 1.0
  const zn = 1.08883
  const fInv = (t: number) => {
    const t3 = t * t * t
    return t3 > 0.008856 ? t3 : (t - 16 / 116) / 7.787
  }
  return {
    x: xn * fInv(x),
    y: yn * fInv(y),
    z: zn * fInv(z)
  }
}

function xyzToRgb(x: number, y: number, z: number): { r: number; g: number; b: number } {
  // sRGB D65
  let r = x * 3.2406 + y * -1.5372 + z * -0.4986
  let g = x * -0.9689 + y * 1.8758 + z * 0.0415
  let b = x * 0.0557 + y * -0.2040 + z * 1.0570
  const comp = (c: number) => {
    return c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055
  }
  r = comp(r)
  g = comp(g)
  b = comp(b)
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v * 255)))
  return { r: clamp(r), g: clamp(g), b: clamp(b) }
}

function labToRgb(L: number, a: number, b: number): { r: number; g: number; b: number } {
  const xyz = labToXyz(L, a, b)
  return xyzToRgb(xyz.x, xyz.y, xyz.z)
}

// =============== 基于 LAB 的 K-Means 调色板提取 ===============
export async function generatePaletteFromExtractedColors(
  pixels: Array<Array<{ r: number; g: number; b: number }>>,
  k: number,
  options?: { maxIterations?: number; sampleRate?: number }
): Promise<Array<{ r: number; g: number; b: number; hex: string; name: string }>> {
  const maxIterations = options?.maxIterations ?? 20
  const sampleRate = options?.sampleRate ?? 0.25 // 采样比例，避免超大数据过慢
  const flat: Array<{ L: number; a: number; b: number }> = []

  // 采样像素并转换到 LAB
  for (let y = 0; y < pixels.length; y++) {
    for (let x = 0; x < (pixels[0]?.length || 0); x++) {
      if (Math.random() > sampleRate) continue
      const p = pixels[y][x]
      const lab = rgbToLab(p.r, p.g, p.b)
      flat.push({ L: lab.l, a: lab.a, b: lab.b })
    }
  }
  if (flat.length === 0) {
    // 若采样为空，退化为全量少量取值
    for (let y = 0; y < pixels.length; y++) {
      for (let x = 0; x < (pixels[0]?.length || 0); x += 4) {
        const p = pixels[y][x]
        const lab = rgbToLab(p.r, p.g, p.b)
        flat.push({ L: lab.l, a: lab.a, b: lab.b })
      }
    }
  }

  const n = flat.length
  const centers: Array<{ L: number; a: number; b: number }> = []
  // kmeans++ 初始化（简化版）
  centers.push(flat[Math.floor(Math.random() * n)])
  while (centers.length < Math.min(k, n)) {
    const dists = flat.map(p => {
      let dMin = Infinity
      for (const c of centers) {
        const d = (p.L - c.L) ** 2 + (p.a - c.a) ** 2 + (p.b - c.b) ** 2
        if (d < dMin) dMin = d
      }
      return dMin
    })
    const sumDist = dists.reduce((acc, v) => acc + v, 0)
    let r = Math.random() * sumDist
    let idx = 0
    for (let i = 0; i < dists.length; i++) {
      r -= dists[i]
      if (r <= 0) { idx = i; break }
    }
    centers.push(flat[idx])
  }

  let assignments = new Array(n).fill(0)
  for (let iter = 0; iter < maxIterations; iter++) {
    // 分配
    for (let i = 0; i < n; i++) {
      let best = 0
      let bestD = Infinity
      for (let c = 0; c < centers.length; c++) {
        const cc = centers[c]
        const p = flat[i]
        const d = (p.L - cc.L) ** 2 + (p.a - cc.a) ** 2 + (p.b - cc.b) ** 2
        if (d < bestD) { bestD = d; best = c }
      }
      assignments[i] = best
    }
    // 更新
    const sums = centers.map(() => ({ L: 0, a: 0, b: 0, count: 0 }))
    for (let i = 0; i < n; i++) {
      const c = assignments[i]
      const p = flat[i]
      const s = sums[c]
      s.L += p.L; s.a += p.a; s.b += p.b; s.count++
    }
    let changed = false
    for (let c = 0; c < centers.length; c++) {
      if (sums[c].count === 0) continue
      const newC = {
        L: sums[c].L / sums[c].count,
        a: sums[c].a / sums[c].count,
        b: sums[c].b / sums[c].count
      }
      const old = centers[c]
      if (Math.abs(newC.L - old.L) > 1e-3 || Math.abs(newC.a - old.a) > 1e-3 || Math.abs(newC.b - old.b) > 1e-3) {
        centers[c] = newC
        changed = true
      }
    }
    if (!changed) break
    // 让出主线程
    await new Promise(r => setTimeout(r))
  }

  const palette = centers.map((c, i) => {
    const rgb = labToRgb(c.L, c.a, c.b)
    const hex = rgbToHex(rgb.r, rgb.g, rgb.b)
    return { r: rgb.r, g: rgb.g, b: rgb.b, hex, name: `自动色${i + 1}` }
  })
  return palette
}

// 计算两个颜色之间的CIELAB距离（更接近人眼感知）
export function colorDistanceLab(
  color1: { r: number; g: number; b: number },
  color2: { r: number; g: number; b: number }
): number {
  const lab1 = rgbToLab(color1.r, color1.g, color1.b)
  const lab2 = rgbToLab(color2.r, color2.g, color2.b)

  return Math.sqrt(
    Math.pow(lab1.l - lab2.l, 2) +
    Math.pow(lab1.a - lab2.a, 2) +
    Math.pow(lab1.b - lab2.b, 2)
  )
}

// 加权RGB距离算法（考虑人眼对不同颜色的敏感度）
export function colorDistanceWeightedRGB(
  color1: { r: number; g: number; b: number },
  color2: { r: number; g: number; b: number }
): number {
  // 人眼对绿色最敏感，红色次之，蓝色最不敏感
  const rWeight = 0.299
  const gWeight = 0.587
  const bWeight = 0.114
  
  return Math.sqrt(
    rWeight * Math.pow(color1.r - color2.r, 2) +
    gWeight * Math.pow(color1.g - color2.g, 2) +
    bWeight * Math.pow(color1.b - color2.b, 2)
  )
}

// RGB转HSV色彩空间
export function rgbToHsv(r: number, g: number, b: number): { h: number; s: number; v: number } {
  r /= 255
  g /= 255
  b /= 255
  
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const diff = max - min
  const sum = max + min
  
  let h = 0
  const s = max === 0 ? 0 : diff / max
  const v = max
  
  if (diff !== 0) {
    switch (max) {
      case r:
        h = ((g - b) / diff) % 6
        break
      case g:
        h = (b - r) / diff + 2
        break
      case b:
        h = (r - g) / diff + 4
        break
    }
    h *= 60
    if (h < 0) h += 360
  }
  
  return { h, s, v }
}

// HSV色彩空间距离算法
export function colorDistanceHSV(
  color1: { r: number; g: number; b: number },
  color2: { r: number; g: number; b: number }
): number {
  const hsv1 = rgbToHsv(color1.r, color1.g, color1.b)
  const hsv2 = rgbToHsv(color2.r, color2.g, color2.b)
  
  // 色相差异（考虑环形特性）
  let hDiff = Math.abs(hsv1.h - hsv2.h)
  if (hDiff > 180) hDiff = 360 - hDiff
  
  // 归一化到[0,1]范围
  const hDiffNorm = hDiff / 180
  const sDiff = Math.abs(hsv1.s - hsv2.s)
  const vDiff = Math.abs(hsv1.v - hsv2.v)
  
  // 权重：色相最重要，饱和度次之，明度最后
  const hWeight = 0.5
  const sWeight = 0.3
  const vWeight = 0.2
  
  return Math.sqrt(
    hWeight * hDiffNorm * hDiffNorm +
    sWeight * sDiff * sDiff +
    vWeight * vDiff * vDiff
  )
}

// CIEDE2000完整算法（工业级精度）
export function colorDistanceCIEDE2000(
  color1: { r: number; g: number; b: number },
  color2: { r: number; g: number; b: number }
): number {
  const lab1 = rgbToLab(color1.r, color1.g, color1.b)
  const lab2 = rgbToLab(color2.r, color2.g, color2.b)
  
  // 简化的CIEDE2000实现（完整版本非常复杂）
  // 这里使用改进的CIELAB距离，更接近CIEDE2000的效果
  
  const L1 = lab1.l, a1 = lab1.a, b1 = lab1.b
  const L2 = lab2.l, a2 = lab2.a, b2 = lab2.b
  
  const L = (L1 + L2) / 2
  const C1 = Math.sqrt(a1 * a1 + b1 * b1)
  const C2 = Math.sqrt(a2 * a2 + b2 * b2)
  const C = (C1 + C2) / 2
  
  const G = 0.5 * (1 - Math.sqrt(Math.pow(C, 7) / (Math.pow(C, 7) + Math.pow(25, 7))))
  
  const a1Prime = a1 * (1 + G)
  const a2Prime = a2 * (1 + G)
  
  const C1Prime = Math.sqrt(a1Prime * a1Prime + b1 * b1)
  const C2Prime = Math.sqrt(a2Prime * a2Prime + b2 * b2)
  const CPrime = (C1Prime + C2Prime) / 2
  
  let h1Prime = Math.atan2(b1, a1Prime) * 180 / Math.PI
  let h2Prime = Math.atan2(b2, a2Prime) * 180 / Math.PI
  
  if (h1Prime < 0) h1Prime += 360
  if (h2Prime < 0) h2Prime += 360
  
  let deltaHPrime = h2Prime - h1Prime
  if (Math.abs(deltaHPrime) > 180) {
    if (deltaHPrime > 0) {
      deltaHPrime -= 360
    } else {
      deltaHPrime += 360
    }
  }
  
  const deltaL = L2 - L1
  const deltaC = C2Prime - C1Prime
  const deltaH = 2 * Math.sqrt(C1Prime * C2Prime) * Math.sin(deltaHPrime * Math.PI / 360)
  
  // 权重函数
  const SL = 1 + (0.015 * Math.pow(L - 50, 2)) / Math.sqrt(20 + Math.pow(L - 50, 2))
  const SC = 1 + 0.045 * CPrime
  const T = 1 - 0.17 * Math.cos((h1Prime - 30) * Math.PI / 180) + 
            0.24 * Math.cos((2 * h1Prime) * Math.PI / 180) + 
            0.32 * Math.cos((3 * h1Prime + 6) * Math.PI / 180) - 
            0.2 * Math.cos((4 * h1Prime - 63) * Math.PI / 180)
  const SH = 1 + 0.015 * CPrime * T
  
  const deltaTheta = 30 * Math.exp(-Math.pow((h1Prime - 275) / 25, 2))
  const RC = 2 * Math.sqrt(Math.pow(CPrime, 7) / (Math.pow(CPrime, 7) + Math.pow(25, 7)))
  const RT = -RC * Math.sin(2 * deltaTheta * Math.PI / 180)
  
  // 最终CIEDE2000距离
  return Math.sqrt(
    Math.pow(deltaL / SL, 2) +
    Math.pow(deltaC / SC, 2) +
    Math.pow(deltaH / SH, 2) +
    RT * (deltaC / SC) * (deltaH / SH)
  )
}

const specialSymbols = ['★', '◆', '●', '■', '▲', '▼', '♦', '♠', '♣', '♥', '☺', '☻', '♫', '♪', '☼', '☽', '☾', '♨', '☢', '☣', '☤', '☥', '☦', '☧', '☨', '☩', '☪', '☫', '☬', '☭', '☮', '☯', '☰', '☱', '☲', '☳', '☴', '☵', '☶', '☷', '☸', '☹', '☺', '☻', '☼', '☽', '☾', '♠', '♣', '♥', '♦']
// 验证颜色值
export function isValidColor(r: number, g: number, b: number): boolean {
  return r >= 0 && r <= 255 && g >= 0 && g <= 255 && b >= 0 && b <= 255
}
const generateSymbol = (index: number): string => {
  const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z']
  const numbers = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0']
  
  if (index <= specialSymbols.length) {
    // 优先使用特殊符号
    return specialSymbols[index - 1]
  } else {
    // 超出特殊符号后，使用智能混用策略
    const remainingId = index - 1 - specialSymbols.length
    const totalRemaining = letters.length + numbers.length
    
    if (remainingId < totalRemaining) {
      // 在字母和数字中随机混用
      const seed = (index - 1) * 7 + 13 // 简单的伪随机种子
      const randomValue = seed % 100
      
      if (randomValue < 60) {
        // 60%概率使用字母
        const letterIndex = remainingId % letters.length
        return letters[letterIndex]
      } else {
        // 40%概率使用数字
        const numberIndex = remainingId % numbers.length
        return numbers[numberIndex]
      }
    } else {
      // 如果还是不够，循环使用
      const cycleIndex = remainingId % totalRemaining
      const seed = (index - 1) * 7 + 13
      const randomValue = seed % 100
      
      if (randomValue < 60) {
        const letterIndex = cycleIndex % letters.length
        return letters[letterIndex]
      } else {
        const numberIndex = cycleIndex % numbers.length
        return numbers[numberIndex]
      }
    }
  }
}
// 生成Excel像素画文件
export async function generateExcelPixelArt(
  colors: Array<Array<{ r: number; g: number; b: number; hex: string }>>,
  customColors: Array<{ r: number; g: number; b: number; hex: string; name: string; id: number }>,
  algorithm: 'rgb' | 'lab' | 'weighted-rgb' | 'hsv' | 'ciede2000' = 'rgb',
  options?: { 
    onProgress?: (progress: number, message?: string) => void, 
    baseFabricColor?: { r: number; g: number; b: number; hex: string },
    labels?: {
      sheetPixelArt: string
      sheetPalette: string
      sheetInfo: string
      sheetSymbols: string
      colIndex: string
      colColorName: string
      colSymbol: string
      colHex: string
      colRgb: string
      colUsage: string
      colPercent: string
      colCoverage: string
      colEstimate: string
      colPreview: string
      colRemark: string
      remarkBackground: string
      userParams: string
      stitchPerStrand: string
      stitchHint: string
      making: string
      done: string
      infoTitle: string
      infoSection1: string
      info1a: string
      info1b: string
      info1c: string
      info1d: string
      info1e: string
      infoSection2: string
      info2a: string
      info2b: string
      info2c: string
      info2d: string
      info2e: string
      info2f: string
      info2g: string
      info2h: string
      info2i: string
      info2j: string
      info2k: string
      infoSection3: string
      info3a: string
      info3b: string
      info3c: string
      infoSection4: string
      info4a: string
      info4b: string
      info4c: string
      infoSection5: string
      info5a: string
      info5b: string
      info5c: string
      info5d: string
      info5e: string
      infoSection6: string
      info6a: string
      info6b: string
      info6c: string
      info6d: string
    }
  }
): Promise<Blob> {
  const ExcelJS: any = await import('exceljs')
  const workbook = new ExcelJS.Workbook()
  const L = options?.labels
  const worksheet = workbook.addWorksheet(L?.sheetPixelArt || '像素画')
  
  const sheetName = L?.sheetPalette || '颜色对照表';
  // Excel 工作表名在公式中需要单引号包裹，且内部单引号需要转义为两个单引号
  const safeSheetRef = `'${sheetName.replace(/'/g, "''")}'`

  const height = colors.length
  const width = colors[0]?.length || 0
  
  // 统计颜色用量
  const colorUsage = new Map<string, { count: number; color: any, symbol:string, rowIndex:number }>()
  let row = 2

  let rowIndex = row;
  // 双层循环：写入像素画并统计用量
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const color = colors[y][x]
      const hex = color.hex.toUpperCase()
      
      let usage;
      // 统计用量
      if (!colorUsage.has(hex)) {
        const exactMatch = customColors.find(c => c.hex.toUpperCase() === hex)
        let symbol:any = ''
        if (color.hex.toUpperCase() !== options?.baseFabricColor?.hex?.toUpperCase()) {
          symbol = generateSymbol(colorUsage.size)
        }
        usage = {
          count: 1,
          color: exactMatch || { hex, name: `颜色${colorUsage.size + 1}` },
          symbol: symbol,
          rowIndex: rowIndex
        }
        colorUsage.set(hex, usage)
        rowIndex++;
      } else {
        usage = colorUsage.get(hex);
        usage!.count++
      }
      
      
      // 写入像素画单元格
      const cell = worksheet.getCell(y + 1, x + 1)
      // 若设置了绣布底色，且像素与底色相同，则跳过背景和符号（留白）
      
      cell.value = { formula: `=IF(${safeSheetRef}!$C$${usage!.rowIndex}="","",${safeSheetRef}!$C$${usage!.rowIndex})` }
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: hex.replace('#', 'FF') }
      }
      
      // 设置单元格内容居中
      cell.alignment = {
        vertical: 'middle',
        horizontal: 'center'
      }
      
      // 每10行10列加粗边框，方便十字绣用户使用
      const isBoldBottom = (y + 1) % 10 === 0
      const isBoldRight = (x + 1) % 10 === 0
      const isLastRow = y === height - 1
      const isLastCol = x === width - 1
      
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFD9D9D9' } },
        left: { style: 'thin', color: { argb: 'FFD9D9D9' } },
        bottom: { style: (isBoldBottom || isLastRow) ? 'thick' : 'thin', color: { argb: (isBoldBottom || isLastRow) ? 'FF000000' : 'FFD9D9D9' } },
        right: { style: (isBoldRight || isLastCol) ? 'thick' : 'thin', color: { argb: (isBoldRight || isLastCol) ? 'FF000000' : 'FFD9D9D9' } }
      }
    }
    
    // 更新进度
    if (options?.onProgress) {
      options.onProgress(y / height * 0.8, L?.making || '生成像素画中...')
    }
  }
  
  // 创建颜色对照表
  const paletteSheet = workbook.addWorksheet(sheetName)
  
  // 设置表头
  paletteSheet.getCell('A1').value = L?.colIndex || '序号'
  paletteSheet.getCell('B1').value = L?.colColorName || '颜色名称'
  paletteSheet.getCell('C1').value = L?.colSymbol || '图形'
  paletteSheet.getCell('D1').value = L?.colHex || '16进制值'
  paletteSheet.getCell('E1').value = L?.colRgb || 'RGB值'
  paletteSheet.getCell('F1').value = L?.colUsage || '用量(格数)'
  paletteSheet.getCell('G1').value = L?.colPercent || '占比'
  paletteSheet.getCell('H1').value = L?.colCoverage || '覆盖系数(格/股)'
  paletteSheet.getCell('I1').value = L?.colEstimate || '估算线量(股)'
  paletteSheet.getCell('J1').value = L?.colPreview || '颜色预览'
  paletteSheet.getCell('K1').value = L?.colRemark || '备注'
  
  // 设置表头样式
  const headerStyle = {
    font: { bold: true, color: { argb: 'FFFFFFFF' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } },
    alignment: { horizontal: 'center' }
  }
  
  for (let col = 1; col <= 11; col++) {
    paletteSheet.getCell(1, col).style = headerStyle
  }
  
  const allCount = width * height;
  // 写入颜色对照表
  
  for (const [hex, info] of colorUsage) {
    // 序号
    paletteSheet.getCell(row, 1).value = row - 1
    // 颜色名称
    paletteSheet.getCell(row, 2).value = info.color.name
    // 图形
    paletteSheet.getCell(row, 3).value = info.symbol
    // 16进制值
    paletteSheet.getCell(row, 4).value = hex
    // RGB值
    paletteSheet.getCell(row, 5).value = `RGB(${info.color.r || 0}, ${info.color.g || 0}, ${info.color.b || 0})`
    // 用量(格数)
    paletteSheet.getCell(row, 6).value = info.count
    // 占比
    paletteSheet.getCell(row, 7).numFmt = '0.00%'
    paletteSheet.getCell(row, 7).value = info.count / allCount
    // 覆盖系数(格/股)
    // paletteSheet.getCell(row, 8).value = 200
    // 估算线量(股)
    paletteSheet.getCell(row, 9).value = { formula: `IF(ISBLANK(F${row}),F${row}/H${row},F${row}/N2)` }
    // 颜色预览
    paletteSheet.getCell(row, 10).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: hex.replace('#', 'FF') }
    }
    if (options?.baseFabricColor && hex.toUpperCase() === options.baseFabricColor.hex.toUpperCase()) {
      
      // 备注
      paletteSheet.getCell(row, 11).value = L?.remarkBackground || "背景色"
    }
    row++
  }
  paletteSheet.getCell(1, 13).value = L?.userParams || '用户参数设置'
  paletteSheet.getCell(2, 13).value = L?.stitchPerStrand || '每股绣线可绣格数'
  paletteSheet.getCell(2, 14).value = 200
  paletteSheet.getCell(2, 15).value = L?.stitchHint || '（请您根据实际使用的绣线和绣布修改此值）'
  // 设置列宽
  worksheet.columns.forEach((column: any) => {
    column.width = 3
  })
  
  // 设置行高为正方形（列宽3对应行高约22.5）
  for (let row = 1; row <= height; row++) {
    worksheet.getRow(row).height = 18
  }
  const colWidth = 15;
  paletteSheet.getColumn(1).width = colWidth
  paletteSheet.getColumn(2).width = colWidth
  paletteSheet.getColumn(3).width = colWidth
  paletteSheet.getColumn(4).width = colWidth
  paletteSheet.getColumn(5).width = colWidth
  paletteSheet.getColumn(6).width = colWidth
  paletteSheet.getColumn(7).width = colWidth
  paletteSheet.getColumn(8).width = colWidth
  paletteSheet.getColumn(9).width = colWidth
  paletteSheet.getColumn(10).width = colWidth
  paletteSheet.getColumn(13).width = colWidth
  paletteSheet.getColumn(14).width = colWidth
  paletteSheet.getColumn(15).width = colWidth
  
  // 添加说明
  const infoSheet = workbook.addWorksheet(L?.sheetInfo || '说明')
  infoSheet.getCell('A1').value = L?.infoTitle || '像素画说明'
  infoSheet.getCell('A1').font = { bold: true, size: 14 }
  
  infoSheet.getCell('A3').value = L?.infoSection1 || '1. 像素画工作表：'
  infoSheet.getCell('A4').value = L?.info1a || '   - 每个单元格代表一个像素'
  infoSheet.getCell('A5').value = L?.info1b || '   - 单元格数值为相应颜色图形符号'
  infoSheet.getCell('A6').value = L?.info1c || '   - 背景色为对应的颜色'
  infoSheet.getCell('A7').value = L?.info1d || '   - 每10行10列有加粗边框，方便定位'
  infoSheet.getCell('A8').value = L?.info1e || '   - 单元格为正方形，图形符号居中显示'
  
  infoSheet.getCell('A10').value = L?.infoSection2 || '2. 颜色对照表：'
  infoSheet.getCell('A11').value = L?.info2a || '   - 序号：颜色在像素画中的编号'
  infoSheet.getCell('A12').value = L?.info2b || '   - 颜色名称：自定义颜色名称或自动生成的名称'
  infoSheet.getCell('A13').value = L?.info2c || '   - 图形：对应的图形符号（可下拉选择）'
  infoSheet.getCell('A14').value = L?.info2d || '   - 16进制值：颜色的16进制表示'
  infoSheet.getCell('A15').value = L?.info2e || '   - RGB值：颜色的RGB值'
  infoSheet.getCell('A16').value = L?.info2f || '   - 用量(格数)：该颜色在像素画中使用的次数'
  infoSheet.getCell('A17').value = L?.info2g || '   - 占比：该颜色占总像素的百分比'
  infoSheet.getCell('A18').value = L?.info2h || '   - 覆盖系数：每股绣线可绣的格数（默认200）'
  infoSheet.getCell('A19').value = L?.info2i || '   - 估算线量：根据用量和覆盖系数计算的绣线股数'
  infoSheet.getCell('A20').value = L?.info2j || '   - 颜色预览：颜色的实际显示'
  infoSheet.getCell('A21').value = L?.info2k || '   - 备注：特殊说明（如背景色）'
  
  infoSheet.getCell('A23').value = L?.infoSection3 || '3. 图形示例工作表：'
  infoSheet.getCell('A24').value = L?.info3a || '   - 展示所有可用的图形符号'
  infoSheet.getCell('A25').value = L?.info3b || '   - 包含序号、图形符号和描述'
  infoSheet.getCell('A26').value = L?.info3c || '   - 可在颜色对照表中通过下拉框选择'
  
  infoSheet.getCell('A28').value = L?.infoSection4 || '4. 公式说明：'
  infoSheet.getCell('A29').value = L?.info4a || '   - 像素画公式：=IF(颜色对照表!$C$行号="","",颜色对照表!$C$行号)'
  infoSheet.getCell('A30').value = L?.info4b || '   - 估算线量公式：=IF(ISBLANK(H行号),F行号/H行号,F行号/N2)'
  infoSheet.getCell('A31').value = L?.info4c || '   - 占比公式：用量/总像素数（自动格式化为百分比）'
  
  infoSheet.getCell('A33').value = L?.infoSection5 || '5. 使用说明：'
  infoSheet.getCell('A34').value = L?.info5a || '   - 可以根据颜色对照表购买对应的绣线或颜料'
  infoSheet.getCell('A35').value = L?.info5b || '   - 用量和占比可以帮助估算材料需求'
  infoSheet.getCell('A36').value = L?.info5c || '   - 可以打印像素画工作表作为制作参考'
  infoSheet.getCell('A37').value = L?.info5d || '   - 可以修改覆盖系数来调整线量估算'
  infoSheet.getCell('A38').value = L?.info5e || '   - 可以自定义图形符号来区分不同颜色'
  
  infoSheet.getCell('A40').value = L?.infoSection6 || '6. 特殊功能：'
  infoSheet.getCell('A41').value = L?.info6a || '   - 背景色识别：与绣布底色相同的像素会留白'
  infoSheet.getCell('A42').value = L?.info6b || '   - 自动符号生成：优先使用特殊符号，超出后使用字母数字'
  infoSheet.getCell('A43').value = L?.info6c || '   - 智能颜色匹配：支持自定义颜色和自动颜色'
  infoSheet.getCell('A44').value = L?.info6d || '   - 实时公式引用：像素画与颜色对照表联动'
  
  // 设置列宽
  infoSheet.getColumn(1).width = 50
  
  // 添加图形示例sheet
  const symbolSheet = workbook.addWorksheet(L?.sheetSymbols || '图形示例')
  symbolSheet.getCell('A1').value = L?.sheetSymbols || '图形示例'
  symbolSheet.getCell('A1').font = { bold: true, size: 14 }
  
  // 循环展示所有特殊符号
  specialSymbols.forEach((symbol, index) => {
    const row = index + 2
    symbolSheet.getCell(row, 1).value = index + 1
    symbolSheet.getCell(row, 2).value = symbol
    symbolSheet.getCell(row, 3).value = `符号${index + 1}`
  })
  
  // 设置图形示例sheet的列宽
  symbolSheet.getColumn(1).width = 8
  symbolSheet.getColumn(2).width = 15
  symbolSheet.getColumn(3).width = 20
  
  // 为颜色对照表的图形列设置下拉框
  // for (let row = 2; row < 2 + colorUsage.size; row++) {
  //   const cell = paletteSheet.getCell(row, 3)
  //   cell.dataValidation = {
  //     type: 'list',
  //     allowBlank: false,
  //     formulae: [`'图形示例'!$B$2:$B$${specialSymbols.length + 1}`],
  //     showErrorMessage: true,
  //     errorTitle: '输入错误',
  //     error: '请从下拉列表中选择图形',
  //     showInputMessage: true,
  //     promptTitle: '选择图形',
  //     prompt: '请从图形示例中选择一个符号'
  //   }
  // }
  
  if (options?.onProgress) {
    options.onProgress(1, L?.done || '完成')
  }
  
  const buffer = await workbook.xlsx.writeBuffer()
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
}




